import { useEffect, useState } from "react";
import axios from "axios";
import "./PrivacyPolicy.css";
import starIcon from './assets/Star 1.svg';
import TopPanel from './components/TopPanel';
import Footer from './components/Footer';

const PrivacyPolicy = () => {
  const [data, setData] = useState(null);
  const [activeSection, setActiveSection] = useState("");

  const sections = [
    { id: "who-are-we", title: "Who are we?" },
    { id: "your-use", title: "Your use of this website" },
    { id: "how-we-collect", title: "How we collect data" },
    { id: "what-personal-data", title: "What personal data do we collect and process?" },
    { id: "how-why-process", title: "How and why we process your data" },
    { id: "how-why-share", title: "How and why we share your data" },
    { id: "non-personal", title: "Non-personal information" },
    { id: "marketing", title: "Marketing communications" },
    { id: "third-party", title: "Consent to third party promotions" },
    { id: "behaviour", title: "Behaviour monitoring & profiling" },
    { id: "international", title: "International transfers" },
    { id: "how-long", title: "How long do we keep your data?" },
    { id: "data-security", title: "Data security" },
    { id: "cookies", title: "Cookies" },
    { id: "your-rights", title: "Your rights" },
    { id: "minors", title: "Minors" },
    { id: "changes", title: "Changes to our privacy policy" },
    { id: "contacting", title: "Contacting Us" }
  ];

  const fetchTermsData = async () => {
    try {
      const response = await axios.get(`/api/v2/article?key=terms`);
      if (response) {
        setData(response.data.data.attributes.value);
      }
    } catch (err) {
      // Handle error
    }
  };

  useEffect(() => {
    fetchTermsData();
  }, []);

  useEffect(() => {
    const handleScroll = () => {
      const sectionElements = sections.map(s => document.getElementById(s.id));
      const scrollPosition = window.scrollY + 200;

      for (let i = sectionElements.length - 1; i >= 0; i--) {
        const element = sectionElements[i];
        if (element && element.offsetTop <= scrollPosition) {
          setActiveSection(sections[i].id);
          break;
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

  return (
    <div className="privacy-page">
      <div className="landing-header-wrapper">
        <TopPanel />
      </div>
      <div className="privacy-page__container">
        <nav className="privacy-page__nav">
          <h3 className="privacy-page__nav-title">Contents</h3>
          {sections.map(section => (
            <button
              key={section.id}
              className={`privacy-page__nav-item ${activeSection === section.id ? 'active' : ''}`}
              onClick={() => scrollToSection(section.id)}
            >
              {section.title}
            </button>
          ))}
        </nav>
        <div className="privacy-page__content-wrapper">
        <h1 className="privacy-page__main-title">Privacy Policy</h1>
        
        <div className="privacy-page__content">
          <p>This privacy statement demonstrates our commitment to your privacy and security and forms part of the WinningPartner Network General Terms & Conditions. The policy contains information about how we use, process and store your data, as well as marketing communications and your legal rights.</p>

          <p>We respect the privacy of our visitors and are committed to preserving your online safety by preserving your privacy at any time you visit or communicate with our site. This Privacy Policy is updated from time to time; therefore, it should be reviewed occasionally. This Policy is not intended to override the terms of any contract you have with us, nor rights you might have under data protection laws.</p>

          <h2 id="who-are-we" className="privacy-page__title">Who are we?</h2>
          <p>WinningPartner Network is a site operated by Traffic Label Limited. As the owner of WinningPartner Network, Traffic Label is principally responsible for looking after your personal data as both a Data Controller and a Data Processor.</p>

          <p>This Privacy Policy concerns Traffic Label's use of data for WinningPartner Network only and does not incorporate data and privacy policies of other Traffic Label-owned properties. For the remainder of this document the following terminology will apply:</p>

          <ul className="privacy-page__list">
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span><strong>Traffic Label</strong> - Data Controller/Processor ("Traffic Label", "TL", us", "we", "brand owner")</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span><strong>WinningPartner Network</strong> - This particular Traffic Label property ("WinningPartner Network", "the site", "the website")</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span><strong>Visitors and customers on our websites</strong> - ("You", "visitors", "members", "affiliates")</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span><strong>Promoted brands and third-parties</strong> - (Advertising partners)</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span><strong>Services offered by WinningPartner Network</strong> - ("Services", "experience")</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span><strong>This document</strong> - ("Privacy Policy", "this policy")</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span><strong>General Data Protection Regulation</strong> - ("GDPR")</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span><strong>Privacy & Electronic Communications Regulations</strong> - ("PECR")</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span><strong>Information Commissioner's Office</strong> - ("ICO")</span>
            </li>
          </ul>

          <h2 id="your-use" className="privacy-page__title">Your use of this website</h2>
          <p>If you don't want us to use or process your Personal Information in the ways we outline above, you should stop using our sites, products and services and contact us to tell us what you do and do not consent to. If you want to withdraw your consent to use or process your data in a certain way, your access to our sites, products and services may be affected. By using our site and interacting with our marketing communications, you are acknowledging that you have read the terms of this Privacy Policy and that you're happy for us to use your Personal Information for the reasons set out above.</p>

          <h2 id="how-we-collect" className="privacy-page__title">How we collect data</h2>
          <p>Data can be collected and processed during the operations of our website. Below is a list of the current ways in which we may currently collect and store your data for WinningPartner Network in accordance with GDPR and PECR.</p>

          <ul className="privacy-page__list">
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span>You register on our website</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span>You subscribe to our website mailing list</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span>You are a member at another site in the Traffic Label group network</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span>You are a member at the website of one of our advertising partners (only where partner's privacy policy permits sharing of data for affiliate marketing purposes)</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span>You subscribe to the mailing list of another site in the network</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span>You communicate directly with WinningPartner Network through a support query</span>
            </li>
          </ul>

          <h2 id="what-personal-data" className="privacy-page__title">What personal data do we collect and process?</h2>
          <p>We may process the following personal data about you:</p>

          <ul className="privacy-page__list">
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span>Full name</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span>Date of birth</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span>Email address</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span>Username</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span>Password</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span>IP address</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span>Website usage</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span>Partner site usage (selected partners)</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span>Business address</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span>Payment information - our services require financial transactions, thus we require your payment information such as bank account number and sort code.</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span>Website usage includes sign up date, last login date, marketing campaign interactions, partner link clicks, viewed pages.</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span>Partner site usage includes clicks, registrations, deposits, wagers, wins, losses and net revenue totals.</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span>Player specific data is handled by individual privacy policies which can be found on individual casino sites.</span>
            </li>
          </ul>

          <h2 id="how-why-process" className="privacy-page__title">How and why we process your data</h2>
          <p>We process personal information as necessary by law and for certain legitimate business purposes in a way that is proportionate and respects your privacy, as outlined here:</p>

          <ul className="privacy-page__list">
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span>Send promotional emails related to our own and third-party products and services (see sections 7 & 8)</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span>To personalise and improve your experience as an affiliate member of the WinningPartner Network</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span>Analyse information in our systems and databases to provide a better and more relevant experience</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span>Providing you with the services described on the site</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span>Verifying your identity when you sign up to WinningPartner Network</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span>Updating you with operational news about the site (changes, service disruptions, security updates)</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span>Monitoring activity on our site and those of our partners to identify potential fraudulent activity and to ensure compliance with the user terms that apply</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span>Managing our relationship with you, e.g. by responding to your comments or queries submitted to us, or asking for your feedback or whether you want to participate in a survey</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span>Improving our products and services</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span>To provide players with a responsible gaming environment As is our duty of care for players that come to us from affiliates.</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span>Providing general administrative and performance functions and activities</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span>Meet or exercise any of our legal obligations or legal rights</span>
            </li>
          </ul>

          <p>You have the right to object to this processing if you wish and can find more details of how to do this further down the page.</p>

          <h2 id="how-why-share" className="privacy-page__title">How and why we share your data</h2>
          <p>We occasionally work with third parties to help manage and deliver our services, which may require access to your personal data. Below is a list of the third parties in question and the reasons your data may be shared.</p>

          <ul className="privacy-page__list">
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span>Our mailing application provider, DotDigital Limited, based in London Bridge, England. We work with DotDigital to send you marketing and support emails.</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span>Service providers or data processors that handle your personal data on our instructions, for example cloud services.</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span>Other sites in the Traffic Label group including, but not limited to, OnlineBingo(.co.uk), for customer analysis, insights and cross-brand promotions.</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span>Our advertising partners for marketing campaign analysis and performance reviews. This helps us to show you only the content that interests you most.</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span>Our white label software providers and license holders for CRM, customer support and to fulfil legal obligations.</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span>Our analytics service providers (such as Google, PushCrew and Facebook), who process personal data for their own purposes as Data Controllers.</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span>If we are under a duty to disclose to comply with a legal obligation or protect our interests or security.</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span>A purchaser or prospective purchaser of all or part of our assets or our business, and their professional advisers, in connection with the purchase.</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span>Regulators and government authorities in connection with our compliance procedures and obligations.</span>
            </li>
          </ul>

          <h2 id="non-personal" className="privacy-page__title">Non-personal information</h2>
          <p>Non-personal Information is non-identifiable information that cannot be used to identify or contact you when considered alone. This stops us from knowing the identity of the user from which the non-personal information was collected. We use non-personal information for reasons including advertising, service improvements, detecting and preventing fraudulent behaviour, statistical analysis and web analytics.</p>

          <h3 className="privacy-page__title">Types of Non-personal Information we collect about you</h3>
          <p>Technical information about the software and hardware you use to visit our site in order to provide you with a better experience when you visit. This can include:</p>

          <ul className="privacy-page__list">
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span>Device manufacturer & model</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span>Type of browser</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span>Operating system</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span>Language preferences</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span>Network provider</span>
            </li>
          </ul>

          <p>Anonymous gameplay information for non-logged in users to monitor marketing campaign performance and to detect fraudulent activity affecting WinningPartner Network or one of our operator partners. Analytics information that we collect about your use of our services including pages viewed, time spent on site, clicks and other related activity. This helps us to troubleshoot common errors, review the user experience and provide better insights for our operator partners.</p>

          <p>We also use third-party analytics software, such as Google Analytics, to collect anonymous user data on the website. Our privacy policy does not apply to these third-party services and you should always refer to their own policies directly. Anonymous information is collected by our services in a way that is not personally identifiable. Our use and disclosure of aggregated or de-identified information is not subject to any restrictions under this Privacy Policy, and we may disclose it to others without limitation and for any purpose, such as for advertising or marketing purposes.</p>

          <p>Please note that if we combine personal information with non-personal information or anonymous information, the combined information will be treated as personal information for as long as it remains combined.</p>

          <h2 id="marketing" className="privacy-page__title">Marketing communications</h2>
          <p>We will use your personal data, such as your email address and telephone number, to send you marketing communications relevant to gambling services, promotions and products. In most cases, we will use our legitimate interests as the lawful basis for processing your data for direct marketing cases, unless we are legally required to seek your explicit consent. Our marketing communications may include the promotion of third-party advertising partners for which this Privacy Policy does not directly apply. We will only send you communications that we believe are of most interest and relevance to you, based on the personal information we hold. You have the right to decline the receipt of marketing promotions from us at any time; simply contact media@trafficlabel.com</p>

          <h2 id="third-party" className="privacy-page__title">Consent to third party promotions</h2>
          <p>As outlined above, our marketing communications will often promote the products and services from our third-party advertising partners, where their privacy policies allow for such promotion, see section 4. In these situations, our communication will retain WinningPartner Network branding, but the content will focus on the specific offer or service from the third-party(ies) in question. Where possible, we screen against third-party suppression lists to ensure you only receive promotions from brands which you have not withdrawn consent from. Please note that WinningPartner Network is not responsible for the privacy practices or the content of these third party sites. We advise our users to check individual terms and conditions/privacy policies where appropriate.</p>

          <h2 id="behaviour" className="privacy-page__title">Behaviour monitoring & profiling</h2>
          <p>We use automated decision tools provided by our email service provider (Dotdigital) to segment our database according to certain actions or inaction of our users. This can include:</p>

          <ul className="privacy-page__list">
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span>Whether or not you opened a particular email</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span>Whether or not you clicked a particular email</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span>Your tendency to interact with emails of a particular theme</span>
            </li>
          </ul>

          <p>This segmentation is handled automatically without human intervention. You can withdraw your consent to be profiled in this way by contacting us via the methods outlined in section 8 of this privacy policy.</p>

          <h2 id="international" className="privacy-page__title">International transfers</h2>
          <p>Personal Information collected by us may be stored and processed in any country where our advertising partners, subsidiaries, suppliers or agents are located. We may also need to transfer data (including Personal Information) to other Traffic Label companies for the supply of our services.</p>

          <p>If this happens, all information will be managed according to and subject to all relevant national, European and international data protection and privacy laws and regulations, including the UK Data Protection Act and the EU General Data Protection Regulations. You hereby consent to transfer of your Personal Information to countries outside the European Union.</p>

          <h2 id="how-long" className="privacy-page__title">How long do we keep your data?</h2>
          <p>We will retain your personal data for as long as reasonably necessary for the purposes listed in Section 4 of this Policy. In some circumstances, we keep your personal data during a certain period to meet for example legal, tax or accounting requirements. We maintain a data retention policy for personal data in our care. Where your personal data is no longer required we will ensure it is either securely deleted or made anonymous.</p>

          <h2 id="data-security" className="privacy-page__title">Data security</h2>
          <p>All of the information that you provide and we collect about you is stored on secure servers, but you should understand that transmission of online data is not always completely secure. We cannot guarantee full protection and security data, only that we take all reasonable action to protect information sent to us electronically. Transmission of any data by you is at your own risk.</p>

          <p>Where applicable you may be given access to sections of our site that require a username and password. You are responsible for the safety, appropriateness and confidentiality of any usernames or passwords created at WinningPartner Network.</p>

          <h2 id="cookies" className="privacy-page__title">Cookies</h2>
          <p>There are instances where we may use cookies to gather information regarding our services in a mathematical collection for our website and advertising. Any information collected will not have any identifying data. It is statistical data about our visitors and how they have used our site. No personal details will be shared that could identify you.</p>

          <p>We may assemble information about your common Internet use with a cookie file. When used, the cookies are downloaded to your computer automatically. The cookie is stored on the hard drive, with transferred information. The data sought by the cookie helps us improve our website and any service offered to you.</p>

          <p>Your browser has the ability to decline cookies via your browser's options. Note: if you do decline the download of cookies, some aspects of our site may not work or allow you access. Our advertisers may download cookies to which we have no control. If used, these cookies are downloaded by clicking on an advertisement found on our website. For full details of how we set and use cookies, please view our Cookie Policy.</p>

          <h2 id="your-rights" className="privacy-page__title">Your rights</h2>
          <p>As a UK or EU citizen, you have a number of rights under the General Data Protection Regulation, which came into place on 25th May 2018. Some of these apply at any time, but some may only apply in certain circumstances.</p>

          <ul className="privacy-page__list">
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span><strong>Access to personal data</strong> - You have a right to request a copy of the personal information that we hold about you.</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span><strong>Correction of personal data</strong> - You can contact us to check and correct any personal data that we are processing about you which you think is incorrect.</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span><strong>Right to withdraw consent</strong> - Where we have relied upon your consent to process your personal data, you have the right to withdraw that consent.</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span><strong>Right to erase</strong> - You can ask us to erase your personal data where there is no legal reason or legitimate interest in keeping it.</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span><strong>Right to data portability</strong> - You can request a copy of the information you have provided us and the right to ask that your personal data is transferred by us directly to another organisation. For security reasons, we will only do this if it is technically possible.</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span><strong>Right to restrict processing of personal data</strong> - In some circumstances you have the right to request that we suspend our processing of your personal data.</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span><strong>Right to object to processing of personal data</strong> - You have the right to object to our use of your personal data which is processed on the basis of our legitimate interests. We will still be allowed to keep your personal data to meet our legitimate interests and regulatory obligations.</span>
            </li>
            <li className="privacy-page__list-item">
              <img src={starIcon} alt="" className="privacy-page__star" />
              <span><strong>Rights relating to automated decision making and profiling</strong> - You have the right not to be subject to a decision which is based solely on an automated process. We do not currently make any decisions about any customer in this way, but if this changes, we will write to tell you.</span>
            </li>
          </ul>

          <h2 id="minors" className="privacy-page__title">Minors</h2>
          <p>The services offered by WinningPartner Network are not designed or directed to persons under the age of 18 or persons under the age of legal consent with respect to the use of the Services of any jurisdiction in which the individual resides, whichever is higher ("Legally of Age"). If you are not Legally of Age, you should not download or use the Services nor provide any Personal Information to us.</p>

          <p>We reserve the right to access and verify any personal information collected from you. In the event that we become aware that a user who is not Legally of Age has shared any information, we may discard such information. If you have any reason to believe that a minor has shared any information with us, please contact us at media@trafficlabel.com.</p>

          <h2 id="changes" className="privacy-page__title">Changes to our privacy policy</h2>
          <p>From time to time we'll have to update our Privacy Policy. Where we make any changes which could impact your rights, we'll do our best to make sure we let you know. This will be done either by contacting you using the details you've given us, or via our sites. At this point, you'll be required to confirm that you've read and accepted the changes before you can continue to use our sites, products or services.</p>

          <p>If, for any reason, we don't or can't confirm your acceptance of the changes, but you continue to use our sites, products or services, we're allowed to believe that you have accepted the changes. All activity undertaken prior to the notification and acceptance of the change will be governed by the terms in place at the time that activity was undertaken.</p>

          <p>If you have any concerns or questions about how we use your data, you should stop using our sites, products and services and contact us for more information. This Privacy Policy is effective from 28th April 2021 and overrides all previous versions.</p>

          <h2 id="contacting" className="privacy-page__title">Contacting Us</h2>
          <p>Please feel free to contact us with queries, requests, or comments you may have about our Privacy Policy or dealings with WinningPartner Network. You can contact us via: media@trafficlabel.com or our online support form.</p>

          <h3 className="privacy-page__title">The Brand Owner details are as follows:</h3>
          <p><strong>Name:</strong> Traffic Label</p>
          <p><strong>Last updated:</strong> January 2026</p>
        </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default PrivacyPolicy;
